import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.25;

export interface ImageHandlers {
  onMouseDown: (e: React.MouseEvent) => void;
  onMouseMove: (e: React.MouseEvent) => void;
  onMouseUp: () => void;
  onMouseLeave: () => void;
  onWheel: (e: React.WheelEvent) => void;
}

export interface ImagePanZoom {
  zoom: number;
  offsetX: number;
  offsetY: number;
  zoomIn: () => void;
  zoomOut: () => void;
  imageHandlers: ImageHandlers;
}

export function useImagePanZoom(resetKey?: string | number | null): ImagePanZoom {
  const [zoom, setZoom] = useState(1);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const isDraggingRef = useRef(false);
  const lastPosRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    setZoom(1);
    setOffsetX(0);
    setOffsetY(0);
  }, [resetKey]);

  const zoomIn = useCallback(() => setZoom((prev) => Math.min(prev + ZOOM_STEP, MAX_ZOOM)), []);

  const zoomOut = useCallback(() => {
    setZoom((prev) => {
      const next = Math.max(prev - ZOOM_STEP, MIN_ZOOM);
      if (next <= 1) {
        setOffsetX(0);
        setOffsetY(0);
      }
      return next;
    });
  }, []);

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (zoom <= 1) return;
      isDraggingRef.current = true;
      lastPosRef.current = { x: e.clientX, y: e.clientY };
    },
    [zoom],
  );

  const onMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDraggingRef.current || zoom <= 1) return;
      const deltaX = e.clientX - lastPosRef.current.x;
      const deltaY = e.clientY - lastPosRef.current.y;
      setOffsetX((prev) => prev + deltaX);
      setOffsetY((prev) => prev + deltaY);
      lastPosRef.current = { x: e.clientX, y: e.clientY };
    },
    [zoom],
  );

  const onMouseUp = useCallback(() => {
    isDraggingRef.current = false;
  }, []);

  const onMouseLeave = useCallback(() => {
    isDraggingRef.current = false;
  }, []);

  const onWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      setZoom((prev) => Math.min(prev + ZOOM_STEP, MAX_ZOOM));
    } else {
      setZoom((prev) => {
        const next = Math.max(prev - ZOOM_STEP, MIN_ZOOM);
        if (next <= 1) {
          setOffsetX(0);
          setOffsetY(0);
        }
        return next;
      });
    }
  }, []);

  const imageHandlers = useMemo(
    () => ({ onMouseDown, onMouseMove, onMouseUp, onMouseLeave, onWheel }),
    [onMouseDown, onMouseMove, onMouseUp, onMouseLeave, onWheel],
  );

  return { zoom, offsetX, offsetY, zoomIn, zoomOut, imageHandlers };
}
